import { type ReactElement, Suspense, useRef } from 'react';
import { useWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import type { MetricInput } from 'components/copilot/risk/RiskMetricsChart.types.ts';
import { usePortfolioMetricsQuery, useRiskDashboardInputSuspenseQuery } from 'generated/graphql.tsx';
import { convertDateRangeToSinceToDate } from 'components/dateRange.utils.ts';
import { calculateLabelToMetricParams, calculatePorfolioMetricsQueryInput } from '../riskMetrics.utils.ts';
import Loader from 'components/technical/Loader/Loader.tsx';
import { RiskMetricsChart } from '../RiskMetricsChart.tsx';
import ColorIndexService from '../ColorIndexService.ts';
import { getDefaultRange, getRangeByLabel, type PredefinedRangeLabel } from 'components/predefinedDateRanges.ts';
import { useSubAccountAssetFilters } from 'components/technical/UseSubAccountAssetFilters.tsx';
import type { PortfolioFilters } from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';

export interface RiskChartWidgetState extends Partial<PortfolioFilters> {
  title: string;
  metricInput: MetricInput;
  warmUpPeriod: string;
  predefinedDateRange: PredefinedRangeLabel;
  showContributionIfAvailable: boolean;
}

const RiskChartWidgetView = (props: { id: string }): ReactElement => {
  const categoryService = useRef(new ColorIndexService());
  const state = useWidgetState<RiskChartWidgetState>(props.id);
  const subAccountAssetFilters = useSubAccountAssetFilters(state);
  const range = getRangeByLabel(state.predefinedDateRange) ?? getDefaultRange();
  const dateRange = range.value();
  const { data: dashboardInput } = useRiskDashboardInputSuspenseQuery({
    variables: {
      subAccountAssetFilters,
    },
  });

  const metricParameters = dashboardInput.portfolio.metricParameters;

  const labelToMetricParams = calculateLabelToMetricParams(metricParameters);
  const metricParamDescriptions = labelToMetricParams?.[state?.metricInput?.metric] ?? [];
  const filters = { subAccountAssetFilter: subAccountAssetFilters, ...convertDateRangeToSinceToDate(dateRange) };

  const { skip, input } = calculatePorfolioMetricsQueryInput({
    metricInput: state.metricInput,
    metricParamDescriptions,
    filters,
  });

  const queryOutput = usePortfolioMetricsQuery({
    variables: {
      input,
    },
    skip,
  });

  return (
    <RiskMetricsChart
      fullHeight
      groups={dashboardInput.assets.assetGroups}
      metricParameters={labelToMetricParams}
      withContribution={state.showContributionIfAvailable}
      title={state.title}
      categoryService={categoryService.current}
      filters={filters}
      metricInput={state.metricInput}
      dateRange={dateRange}
      warmUpPeriod={state.warmUpPeriod}
      queryOutput={queryOutput}
    />
  );
};

const RiskChartWidgetViewContainer = (props: { id: string }): ReactElement => {
  return (
    <Suspense fallback={<Loader />}>
      <RiskChartWidgetView {...props} />
    </Suspense>
  );
};

export default RiskChartWidgetViewContainer;
